import React, { useState } from 'react';
import {
  PrimaryButton,
  SecondaryButton,
  GhostButton,
  DangerButton,
  SuccessButton,
  IconButton,
} from '../../components/buttons';
import {
  PlusIcon,
  EditIcon,
  TrashIcon,
  CheckIcon,
  SearchIcon,
} from '../../icons';
import { surfaces, textColors, typography, borders } from '../../tokens';

/**
 * ButtonShowcase
 * Displays all button variants with interactive examples
 * Theme-aware for dark mode support
 */
const ButtonShowcase = () => {
  const [loadingStates, setLoadingStates] = useState({});

  const handleLoadingDemo = (buttonId) => {
    setLoadingStates(prev => ({ ...prev, [buttonId]: true }));
    setTimeout(() => {
      setLoadingStates(prev => ({ ...prev, [buttonId]: false }));
    }, 2000);
  };

  const CodeBlock = ({ code }) => (
    <pre className="bg-gray-900 dark:bg-dark-950 text-gray-100 dark:text-dark-200 p-4 rounded-lg text-xs overflow-x-auto">
      <code>{code}</code>
    </pre>
  );

  return (
    <div className="space-y-12">
      {/* Primary Button */}
      <section>
        <h2 className={`${typography.h2} ${textColors.bright} mb-4`}>Primary Button</h2>
        <p className={`${typography.body} ${textColors.secondary} mb-6`}>
          Use for primary actions and main CTAs. Blue background with white text.
        </p>

        {/* Sizes */}
        <div className="mb-6">
          <h3 className={`${typography.h4} ${textColors.primary} mb-3`}>Sizes</h3>
          <div className={`flex flex-wrap items-center gap-4 p-6 ${surfaces.primary} rounded-lg border ${borders.default}`}>
            <PrimaryButton size="sm">Small</PrimaryButton>
            <PrimaryButton size="md">Medium</PrimaryButton>
            <PrimaryButton size="lg">Large</PrimaryButton>
          </div>
        </div>

        {/* States */}
        <div className="mb-6">
          <h3 className={`${typography.h4} ${textColors.primary} mb-3`}>States</h3>
          <div className={`flex flex-wrap items-center gap-4 p-6 ${surfaces.primary} rounded-lg border ${borders.default}`}>
            <PrimaryButton>Default</PrimaryButton>
            <PrimaryButton isLoading={loadingStates.primary}>
              {loadingStates.primary ? 'Loading...' : 'Click to Load'}
            </PrimaryButton>
            <PrimaryButton disabled>Disabled</PrimaryButton>
          </div>
          <div className="mt-2">
            <SecondaryButton size="sm" onClick={() => handleLoadingDemo('primary')}>
              Trigger Loading State
            </SecondaryButton>
          </div>
        </div>

        {/* With Icons */}
        <div className="mb-6">
          <h3 className={`${typography.h4} ${textColors.primary} mb-3`}>With Icons</h3>
          <div className={`flex flex-wrap items-center gap-4 p-6 ${surfaces.primary} rounded-lg border ${borders.default}`}>
            <PrimaryButton leftIcon={<PlusIcon size="sm" />}>Create New</PrimaryButton>
            <PrimaryButton rightIcon={<CheckIcon size="sm" />}>Save Changes</PrimaryButton>
            <PrimaryButton leftIcon={<SearchIcon size="sm" />} rightIcon={<CheckIcon size="sm" />}>
              Search & Confirm
            </PrimaryButton>
          </div>
        </div>

        {/* Code Example */}
        <div className="mb-6">
          <h3 className={`${typography.h4} ${textColors.primary} mb-3`}>Code Example</h3>
          <CodeBlock code={`import { PrimaryButton } from '../design-system';

<PrimaryButton 
  onClick={handleClick} 
  size="md" 
  isLoading={loading}
  leftIcon={<PlusIcon />}
>
  Create New
</PrimaryButton>`} />
        </div>
      </section>

      {/* Secondary Button */}
      <section>
        <h2 className={`${typography.h2} ${textColors.bright} mb-4`}>Secondary Button</h2>
        <p className={`${typography.body} ${textColors.secondary} mb-6`}>
          Use for secondary actions and alternatives. White background with border.
        </p>

        <div className="mb-6">
          <h3 className={`${typography.h4} ${textColors.primary} mb-3`}>Variants</h3>
          <div className={`flex flex-wrap items-center gap-4 p-6 ${surfaces.primary} rounded-lg border ${borders.default}`}>
            <SecondaryButton>Default</SecondaryButton>
            <SecondaryButton variant="ghost">Ghost</SecondaryButton>
            <SecondaryButton variant="danger">Danger</SecondaryButton>
            <SecondaryButton isLoading={loadingStates.secondary}>
              {loadingStates.secondary ? 'Loading...' : 'Click to Load'}
            </SecondaryButton>
            <SecondaryButton disabled>Disabled</SecondaryButton>
          </div>
          <div className="mt-2">
            <SecondaryButton size="sm" onClick={() => handleLoadingDemo('secondary')}>
              Trigger Loading State
            </SecondaryButton>
          </div>
        </div>

        <div className="mb-6">
          <h3 className={`${typography.h4} ${textColors.primary} mb-3`}>Code Example</h3>
          <CodeBlock code={`import { SecondaryButton } from '../design-system';

<SecondaryButton onClick={handleCancel} variant="ghost">
  Cancel
</SecondaryButton>`} />
        </div>
      </section>

      {/* Ghost Button */}
      <section>
        <h2 className={`${typography.h2} ${textColors.bright} mb-4`}>Ghost Button</h2>
        <p className={`${typography.body} ${textColors.secondary} mb-6`}>
          Use for minimal visual weight. Transparent background, no border.
        </p>

        <div className={`flex flex-wrap items-center gap-4 p-6 ${surfaces.primary} rounded-lg border ${borders.default} mb-6`}>
          <GhostButton size="sm">Small Ghost</GhostButton>
          <GhostButton size="md">Medium Ghost</GhostButton>
          <GhostButton size="lg">Large Ghost</GhostButton>
          <GhostButton disabled>Disabled</GhostButton>
        </div>

        <div className="mb-6">
          <h3 className={`${typography.h4} ${textColors.primary} mb-3`}>Code Example</h3>
          <CodeBlock code={`import { GhostButton } from '../design-system';

<GhostButton onClick={handleClose}>
  Close
</GhostButton>`} />
        </div>
      </section>

      {/* Success Button */}
      <section>
        <h2 className={`${typography.h2} ${textColors.bright} mb-4`}>Success Button</h2>
        <p className={`${typography.body} ${textColors.secondary} mb-6`}>
          Use for positive actions like create, confirm, complete. Green background.
        </p>

        <div className={`flex flex-wrap items-center gap-4 p-6 ${surfaces.primary} rounded-lg border ${borders.default} mb-6`}>
          <SuccessButton size="sm">Create</SuccessButton>
          <SuccessButton size="md" leftIcon={<CheckIcon size="sm" />}>Confirm</SuccessButton>
          <SuccessButton size="lg">Complete</SuccessButton>
          <SuccessButton isLoading={loadingStates.success}>
            {loadingStates.success ? 'Processing...' : 'Click to Process'}
          </SuccessButton>
          <SuccessButton disabled>Disabled</SuccessButton>
        </div>
        <div className="mb-6">
          <SecondaryButton size="sm" onClick={() => handleLoadingDemo('success')}>
            Trigger Loading State
          </SecondaryButton>
        </div>

        <div className="mb-6">
          <h3 className={`${typography.h4} ${textColors.primary} mb-3`}>Code Example</h3>
          <CodeBlock code={`import { SuccessButton } from '../design-system';

<SuccessButton 
  onClick={handleCreate}
  leftIcon={<PlusIcon />}
>
  Create New
</SuccessButton>`} />
        </div>
      </section>

      {/* Danger Button */}
      <section>
        <h2 className={`${typography.h2} ${textColors.bright} mb-4`}>Danger Button</h2>
        <p className={`${typography.body} ${textColors.secondary} mb-6`}>
          Use for destructive actions like delete, remove. Red background.
        </p>

        <div className={`flex flex-wrap items-center gap-4 p-6 ${surfaces.primary} rounded-lg border ${borders.default} mb-6`}>
          <DangerButton size="sm">Delete</DangerButton>
          <DangerButton size="md" leftIcon={<TrashIcon size="sm" />}>Remove</DangerButton>
          <DangerButton size="lg">Permanently Delete</DangerButton>
          <DangerButton isLoading={loadingStates.danger}>
            {loadingStates.danger ? 'Deleting...' : 'Click to Delete'}
          </DangerButton>
          <DangerButton disabled>Disabled</DangerButton>
        </div>
        <div className="mb-6">
          <SecondaryButton size="sm" onClick={() => handleLoadingDemo('danger')}>
            Trigger Loading State
          </SecondaryButton>
        </div>

        <div className="mb-6">
          <h3 className={`${typography.h4} ${textColors.primary} mb-3`}>Code Example</h3>
          <CodeBlock code={`import { DangerButton } from '../design-system';

<DangerButton 
  onClick={handleDelete}
  leftIcon={<TrashIcon />}
>
  Delete Item
</DangerButton>`} />
        </div>
      </section>

      {/* Icon Button */}
      <section>
        <h2 className={`${typography.h2} ${textColors.bright} mb-4`}>Icon Button</h2>
        <p className={`${typography.body} ${textColors.secondary} mb-6`}>
          Use for compact UI with icon-only buttons. Multiple variants available.
        </p>

        <div className="mb-6">
          <h3 className={`${typography.h4} ${textColors.primary} mb-3`}>Variants</h3>
          <div className={`flex flex-wrap items-center gap-4 p-6 ${surfaces.primary} rounded-lg border ${borders.default}`}>
            <IconButton icon={<EditIcon />} title="Edit" variant="primary" />
            <IconButton icon={<TrashIcon />} title="Delete" variant="secondary" />
            <IconButton icon={<SearchIcon />} title="Search" variant="ghost" />
            <IconButton icon={<CheckIcon />} title="Confirm" variant="success" />
            <IconButton icon={<TrashIcon />} title="Delete" variant="danger" />
          </div>
        </div>

        <div className="mb-6">
          <h3 className={`${typography.h4} ${textColors.primary} mb-3`}>Sizes</h3>
          <div className={`flex flex-wrap items-center gap-4 p-6 ${surfaces.primary} rounded-lg border ${borders.default}`}>
            <IconButton icon={<EditIcon size="sm" />} title="Small" size="sm" variant="primary" />
            <IconButton icon={<EditIcon size="md" />} title="Medium" size="md" variant="primary" />
            <IconButton icon={<EditIcon size="lg" />} title="Large" size="lg" variant="primary" />
          </div>
        </div>

        <div className="mb-6">
          <h3 className={`${typography.h4} ${textColors.primary} mb-3`}>Code Example</h3>
          <CodeBlock code={`import { IconButton, EditIcon } from '../design-system';

<IconButton 
  icon={<EditIcon />} 
  title="Edit" 
  variant="primary"
  onClick={handleEdit}
/>`} />
        </div>
      </section>

      {/* All Together */}
      <section>
        <h2 className={`${typography.h2} ${textColors.bright} mb-4`}>Button Comparison</h2>
        <p className={`${typography.body} ${textColors.secondary} mb-6`}>
          All button variants side by side for easy comparison.
        </p>

        <div className="overflow-x-auto">
          <table className={`w-full border-collapse ${surfaces.primary} rounded-lg overflow-hidden shadow-sm`}>
            <thead>
              <tr className={`${surfaces.secondary} border-b ${borders.default}`}>
                <th className={`text-left p-4 text-sm font-semibold ${textColors.primary}`}>Button Type</th>
                <th className={`text-left p-4 text-sm font-semibold ${textColors.primary}`}>Default</th>
                <th className={`text-left p-4 text-sm font-semibold ${textColors.primary}`}>With Icon</th>
                <th className={`text-left p-4 text-sm font-semibold ${textColors.primary}`}>Disabled</th>
              </tr>
            </thead>
            <tbody>
              <tr className={`border-b ${borders.light}`}>
                <td className={`p-4 text-sm font-medium ${textColors.secondary}`}>Primary</td>
                <td className="p-4"><PrimaryButton size="sm">Button</PrimaryButton></td>
                <td className="p-4"><PrimaryButton size="sm" leftIcon={<PlusIcon size="sm" />}>Button</PrimaryButton></td>
                <td className="p-4"><PrimaryButton size="sm" disabled>Button</PrimaryButton></td>
              </tr>
              <tr className={`border-b ${borders.light}`}>
                <td className={`p-4 text-sm font-medium ${textColors.secondary}`}>Secondary</td>
                <td className="p-4"><SecondaryButton size="sm">Button</SecondaryButton></td>
                <td className="p-4"><SecondaryButton size="sm" leftIcon={<EditIcon size="sm" />}>Button</SecondaryButton></td>
                <td className="p-4"><SecondaryButton size="sm" disabled>Button</SecondaryButton></td>
              </tr>
              <tr className={`border-b ${borders.light}`}>
                <td className={`p-4 text-sm font-medium ${textColors.secondary}`}>Ghost</td>
                <td className="p-4"><GhostButton size="sm">Button</GhostButton></td>
                <td className="p-4"><GhostButton size="sm" leftIcon={<CheckIcon size="sm" />}>Button</GhostButton></td>
                <td className="p-4"><GhostButton size="sm" disabled>Button</GhostButton></td>
              </tr>
              <tr className={`border-b ${borders.light}`}>
                <td className={`p-4 text-sm font-medium ${textColors.secondary}`}>Success</td>
                <td className="p-4"><SuccessButton size="sm">Button</SuccessButton></td>
                <td className="p-4"><SuccessButton size="sm" leftIcon={<CheckIcon size="sm" />}>Button</SuccessButton></td>
                <td className="p-4"><SuccessButton size="sm" disabled>Button</SuccessButton></td>
              </tr>
              <tr className={`border-b ${borders.light}`}>
                <td className={`p-4 text-sm font-medium ${textColors.secondary}`}>Danger</td>
                <td className="p-4"><DangerButton size="sm">Button</DangerButton></td>
                <td className="p-4"><DangerButton size="sm" leftIcon={<TrashIcon size="sm" />}>Button</DangerButton></td>
                <td className="p-4"><DangerButton size="sm" disabled>Button</DangerButton></td>
              </tr>
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
};

export default ButtonShowcase;
